import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import * as approvalTypeApi from "../../api/approval/type";
import TinyMCEeditor from "../../components/const/TinyMCEeditor";

export default function ApprovalTypeEdit() {
  const { id: projectId, typeId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const editorRef = useRef(null);

  const isNew = location.state?.mode === "add" || typeId === "new";

  const [form, setForm] = useState({
    id: "",
    name: "",
    code: "",
    title: "",
    content: "",
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const editorKey = useMemo(() => `type-${form.id || "new"}`, [form.id]);

  useEffect(() => {
    if (isNew || !projectId || !typeId) {
      setForm({ id: "", name: "", code: "", title: "", content: "" });
      return;
    }

    setLoading(true);
    approvalTypeApi
      .getTypeDetail(projectId, typeId)
      .then((data) => setForm(data))
      .catch(() => alert("유형을 불러오지 못했습니다."))
      .finally(() => setLoading(false));
  }, [projectId, typeId, isNew]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name?.trim()) {
      alert("양식명을 입력해주세요.");
      return;
    }

    try {
      setSaving(true);
      const payload = {
        ...form,
        content: editorRef.current?.getContent() || form.content,
      };

      await approvalTypeApi.saveType(projectId, payload);
      alert(isNew ? "새 유형이 추가되었습니다." : "저장되었습니다.");
      navigate(`/project/${projectId}/approval/type`);
    } catch (err) {
      alert("저장 실패");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 본문 영역 */}
      <main className="mx-auto max-w-md px-4 pt-4 pb-28">
        {/* 페이지 타이틀 */}
        <header className="mb-5">
          <p className="text-gray-medium">결재 설정</p>
          <h2 className="text-title">
            {isNew ? "새 문서유형 등록" : "문서유형 편집"}
          </h2>
          <p className="text-muted mt-1">
            결재 문서의 양식을 {isNew ? "등록" : "수정"}합니다.
          </p>
        </header>

        {loading && !isNew ? (
          <div className="text-center text-muted py-12">불러오는 중...</div>
        ) : (
          <form onSubmit={handleSave} className="space-y-4">
            {/* 양식명 */}
            <section>
              <label className="text-gray-medium mb-1 block">양식명</label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                className="w-full rounded-2xl border border-gray-300 bg-white px-3 py-2 text-body focus:border-black focus:outline-none focus:ring-2 focus:ring-black/70"
                placeholder="예: 품질, 설계, 일반"
              />
            </section>

            {/* 코드 */}
            <section>
              <label className="text-gray-medium mb-1 block">코드</label>
              <input
                name="code"
                value={form.code}
                onChange={handleChange}
                disabled={!isNew}
                className={`w-full rounded-2xl border border-gray-300 bg-white px-3 py-2 text-body focus:border-black focus:outline-none focus:ring-2 focus:ring-black/70 ${
                  !isNew ? "bg-gray-100 text-gray-500 cursor-not-allowed" : ""
                }`}
                placeholder="영어 대문자 2자"
                maxLength={2}
              />
            </section>

            {/* 제목 예시 */}
            <section>
              <label className="text-gray-medium mb-1 block">제목 예시</label>
              <input
                name="title"
                value={form.title}
                onChange={handleChange}
                className="w-full rounded-2xl border border-gray-300 bg-white px-3 py-2 text-body focus:border-black focus:outline-none focus:ring-2 focus:ring-black/70"
                placeholder="결재 제목 예시"
              />
            </section>

            {/* 내용 템플릿 (TinyMCE) */}
            <section className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
              <div className="border-b border-gray-100 px-4 py-2">
                <label className="text-gray-medium">내용 템플릿</label>
              </div>
              {/* 에디터 영역: 고정 높이 + 내부 스크롤 */}
              <div
                className="relative"
                style={{ height: "280px", overflow: "hidden" }}
              >
                <div className="absolute inset-0 overflow-auto">
                  <TinyMCEeditor
                    key={editorKey}
                    editorRef={editorRef}
                    initialValue={form.content}
                  />
                </div>
              </div>
            </section>
          </form>
        )}
      </main>

      {/* 하단 액션바 */}
      {!loading && (
        <footer className="fixed inset-x-0 bottom-0 bg-white border-t px-5 py-3 safe-area-bottom">
          <div className="max-w-md mx-auto grid grid-cols-2 gap-3">
            <button
              type="button"
              className="btn-cancel w-full h-11 rounded-full"
              onClick={() => navigate(-1)}
            >
              취소
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="btn-save w-full h-11 rounded-full"
            >
              {saving ? "저장 중…" : isNew ? "등록" : "저장"}
            </button>
          </div>
        </footer>
      )}
    </div>
  );
}
